import React from 'react';
import cn from 'classnames';
import { observer } from 'mobx-react';

import { RailStoreV2 } from '@kaltura-ott/tvpil-shared';

import AirTime from 'components/presenters/AirTime/AirTime';
import {
  ContentMarker,
  ExpiresBadge,
  FavoriteIcon,
  NewEpisodeBadge,
  PurchaseIcon,
  RentedBadge,
} from 'components/presenters/icons';
import PlayButton from 'components/widgets/buttons/PlayButton/PlayButton';

import { useAssetCardContext } from '../AssetCardContext/AssetCardContext';
import { AssetCardProgramChannelInfo, AssetCardProgressBar } from '../components';
import { AssetCardRatingInfo } from '../components/AssetCardRatingInfo/AssetCardRatingInfo';

import styles from './AssetCardContent.module.scss';

const COMPONENT_NAME = 'Rail.RailCard';

function AssetCardContent() {
  const {
    listStore,
    resolvedProps: {
      isLinearAsset,
      isProgramAsset,
      isDisplayCartIcon,
      contentMarker,
      isDisplayContentMarker,
      isNewEpisode,
      isRented,
      isContinueWatching,
      isShowFutureProgramInfo,
      isContentRestricted,
      catchUpAirDate,
      isHero,
      isPurchasable,
      isRecordExpires,
      recordingExpirationInDays,
      isShowPlayButton,
      isShowedProgressBar,
      linearId,
      isOnGoingRecord,
      isSeriesRecord,
    },
  } = useAssetCardContext();

  const isForceOverlayShowing = isOnGoingRecord && !isSeriesRecord;
  const isFutureUi = !isHero && !isContinueWatching && (isShowFutureProgramInfo || isForceOverlayShowing);

  return (
    <div
      className={cn(styles.inner, { [styles.programAsset]: isProgramAsset }, { [styles.linearAsset]: isLinearAsset })}
    >
      {isDisplayCartIcon && <PurchaseIcon className={styles.cart} isBadge />}
      {isDisplayContentMarker && <ContentMarker className={styles.contentMarker} contentMarker={contentMarker!} />}
      {isNewEpisode && <NewEpisodeBadge translationId={COMPONENT_NAME} className={styles.newEpisodeBadge} />}
      {isRented && <RentedBadge className={styles.rentedBadge} translationId={COMPONENT_NAME} />}
      {!isContentRestricted && (
        <>
          {isFutureUi && (
            <div data-type='futureProgram' className={styles.futureProgram}>
              <AirTime catchUpAirDate={catchUpAirDate} showNowLabel={isOnGoingRecord} />
            </div>
          )}

          <AssetCardRatingInfo translationId={COMPONENT_NAME} />

          {isShowPlayButton && <PlayButton className={styles.playBtn} />}
          {isShowedProgressBar && <AssetCardProgressBar />}

          {isRecordExpires && (
            <ExpiresBadge
              className={styles.expires}
              translationId={COMPONENT_NAME}
              expiredInDays={recordingExpirationInDays!}
            />
          )}
        </>
      )}
      {isHero && (
        <div className={styles.channelLarge}>
          <AssetCardProgramChannelInfo />
          <FavoriteIcon
            className={styles.favoriteChannel}
            isPurchasable={isPurchasable}
            linearId={linearId}
            presentOnRail={(listStore as RailStoreV2).presentFavoriteIndicator}
          />
        </div>
      )}
    </div>
  );
}

export default observer(AssetCardContent);
